import {
  ColumnsProps,
  DraggingTableHeaders,
  FilterForm,
  IconFont,
  OptionType,
  SignTitle,
  SvgIcon,
} from '@/components'
import {
  Breadcrumb,
  Button,
  Card,
  Divider,
  Dropdown,
  MenuProps,
  Modal,
  Pagination,
  Space,
  Tooltip,
  Typography,
} from 'antd'
import { DownOutlined } from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { t } from '@/i18n'
import { useDeleteSecurityDomain, useGetSecurityDomainList } from './utils'
import SdDetail from './sd-detail'
import AddDrawer from './add-drawer'

interface PropsType {}

export const SecurityDomain: React.FC<PropsType> = () => {
  const [searchParams, setSearchParams] = useState({ search: '', page: 1, limit: 10 })
  const [filterParams, setFilterParams] = useState<{ [k: string]: string | undefined }>()
  const [detailParams, setDetailParams] = useState<{ visible: boolean; item: any }>({
    visible: false,
    item: '',
  })
  const [deleteParams, setDeleteParams] = useState<{ visible: boolean; id: string[] }>({
    visible: false,
    id: [],
  })
  const [addParams, setAddParams] = useState<{
    type: string
    id: string
    visible: boolean
    name?: string
  }>({
    type: '',
    id: '',
    visible: false,
  })

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])

  const [securityDomainList, loading, getSecurityDomainList] = useGetSecurityDomainList()
  const [deleteSecurityDomain, deleteLoading] = useDeleteSecurityDomain()

  const onClick: MenuProps['onClick'] = ({ key }) => {
    if (key === '1') {
      setAddParams(origin => ({ ...origin, type: 'edit', visible: true }))
    } else {
      setDeleteParams(origin => ({ ...origin, visible: true }))
    }
  }

  const handleDelete = () => {
    deleteSecurityDomain(deleteParams.id)
      .then(() => {
        setDeleteParams({ visible: false, id: [] })
        fetchList()
      })
      .catch(_ => {})
  }

  const option = {
    search: {
      type: 'input',
      label: t('安全域名称'),
    },
  } as OptionType

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <>
          <IconFont type="icon-bianji" />
          <span style={{ marginLeft: 6 }}>{t('编辑')}</span>
        </>
      ),
    },
    {
      key: '2',
      label: (
        <>
          <IconFont type="icon-shanchu-m" />
          <span style={{ marginLeft: 6 }}>{t('删除')}</span>
        </>
      ),
    },
  ]

  const columns = [
    {
      title: t('安全域名称'),
      dataIndex: 'name',
      key: 'name',
      width: 300,
    },
    {
      title: t('钓鱼邮箱'),
      dataIndex: 'phish_email_count',
      key: 'phish_email_count',
      width: 220,
      render: (_, { phish_email_count }) => <Item color="#558492">{phish_email_count || 0}</Item>,
    },
    {
      title: t('Web应用'),
      dataIndex: 'site_count',
      key: 'site_count',
      width: 200,
      render: (_, { site_count }) => <Item color="#CF3F3D">{site_count}</Item>,
    },
    {
      title: t('客户端节点'),
      dataIndex: 'agent_count',
      key: 'agent_count',
      width: 200,
      render: (_, { agent_count }) => <Item color="#2D98FF">{agent_count}</Item>,
    },
    {
      title: (
        <div>
          <span>{t('验证邮箱')}</span>
          <Tooltip
            title={t('为客户端中邮件安全配置的邮箱')}
            getPopupContainer={() => document.body}
          >
            <IconFont type="icon-tishi" style={{ marginLeft: 8, color: '#999' }} />
          </Tooltip>
        </div>
      ),
      dataIndex: 'email_count',
      key: 'email_count',
      width: 250,
      render: (_, { email_count }) => <Item color="#FFA709">{email_count}</Item>,
    },
    {
      title: t('操作'),
      width: '200px',
      render: (_, record) => (
        <Space split={<Divider type="vertical" />} style={{ width: '100%' }}>
          <Typography.Link onClick={() => setDetailParams({ visible: true, item: record })}>
            <IconFont type="icon-chakan" style={{ fontSize: '16px' }} />
            <span style={{ marginLeft: 8 }}>{t('查看')}</span>
          </Typography.Link>

          {record.id !== '0' && (
            <Dropdown
              menu={{ items, onClick }}
              trigger={['hover']}
              getPopupContainer={() => document.body}
              onOpenChange={status => {
                if (status) {
                  setAddParams(origin => ({ ...origin, id: record.id, name: record.name }))
                  setDeleteParams(origin => ({ ...origin, id: [record.id] }))
                } else {
                  setAddParams({ type: '', id: '', visible: false })
                  setDeleteParams({ visible: false, id: [] })
                }
              }}
            >
              <div style={{ cursor: 'pointer', color: '#4238c9' }}>
                <span>{t('更多')} </span>
                <DownOutlined />
              </div>
            </Dropdown>
          )}
        </Space>
      ),
    },
  ] as ColumnsProps

  const rowSelection = {
    type: 'checkbox',
    selectedRowKeys,
    onChange: (keys: string[]) => {
      setSelectedRowKeys(keys)
    },
    renderCell: (checked, record, index, originNode) => {
      if (record.id === '0') {
        return null
      }
      return originNode
    },
  }

  const fetchList = () => {
    getSecurityDomainList(searchParams).catch(_ => {})
  }

  useEffect(() => {
    fetchList()
  }, [searchParams])

  return (
    <Container>
      <Breadcrumb style={{ margin: '12px 0', fontSize: '12px' }}>
        <Breadcrumb.Item>{t('配置中心')}</Breadcrumb.Item>
        <Breadcrumb.Item>{t('安全域')}</Breadcrumb.Item>
      </Breadcrumb>
      <CardWrapper
        title={<SignTitle>{t('安全域列表')}</SignTitle>}
        extra={
          <Button
            type="primary"
            onClick={() => setAddParams({ type: 'add', id: '', visible: true })}
          >
            {t('添加安全域')}
          </Button>
        }
      >
        <FilterForm
          option={option}
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          setSearchParams={setSearchParams}
          onReset={() => setSearchParams({ search: '', page: 1, limit: 10 })}
        />
        <div style={{ padding: '0 20px' }}>
          <DraggingTableHeaders
            columns={columns}
            loading={loading}
            dataSource={securityDomainList}
            rowSelection={rowSelection}
            selectedRowKeys={selectedRowKeys as string[]}
            setSelectedRowKeys={setSelectedRowKeys}
            pagination={false}
            batchOperation={
              <Space
                split={<Divider type={'vertical'} />}
                onClick={e => {
                  e.stopPropagation()
                }}
              >
                <div>{t('批量操作')}</div>
                <Delete
                  active={selectedRowKeys.length > 0}
                  onClick={() => {
                    setDeleteParams({ visible: true, id: selectedRowKeys })
                  }}
                >
                  <SvgIcon name={'batch_remove'} className="icon-delete" />
                  {t('批量删除')}
                </Delete>
              </Space>
            }
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 20px' }}>
          <Pagination
            showSizeChanger
            showQuickJumper
            current={searchParams.page}
            pageSize={searchParams.limit}
            total={securityDomainList.length}
            showTotal={total => `${t('共')} ${total} ${t('条')}`}
            onChange={(page, pageSize) => {
              setSearchParams({ ...searchParams, page, limit: pageSize })
            }}
          />
        </div>
      </CardWrapper>
      <SdDetail
        open={detailParams.visible}
        item={detailParams.item}
        onCancel={() => setDetailParams({ visible: false, item: '' })}
      />
      <Modal
        centered
        title={<SignTitle show>{t('提示')}</SignTitle>}
        open={deleteParams.visible}
        onCancel={() => setDeleteParams({ visible: false, id: [] })}
        onOk={handleDelete}
        confirmLoading={deleteLoading}
      >
        <IconFont type={'icon-tixingshixin'} style={{ color: '#faad14', marginRight: 10 }} />
        <span>{t('此操作将永久删除，是否继续？')}</span>
      </Modal>
      <AddDrawer
        type={addParams.type}
        id={addParams.id}
        visible={addParams.visible}
        name={addParams.name}
        onCancel={() => setAddParams({ type: '', id: '', visible: false })}
        updateList={fetchList}
      />
    </Container>
  )
}

export default SecurityDomain

const Container = styled.div`
  height: 100%;
  background: #f1f3f5;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  padding-bottom: 16px;
`

const CardWrapper = styled(Card)`
  flex: 1;
  padding-bottom: 16px;
  .ant-card-head-title {
    padding: 0;
  }
  .ant-card-extra {
    padding: 0;
  }
  .ant-card-head-wrapper {
    margin-top: 20px;
  }

  .ant-card-body {
    padding: 16px 0px;
  }
`

interface ItemProps {
  color: string
}

const Item = styled.div<ItemProps>`
  display: inline-block;
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 4px;
  border: 1px solid ${props => props.color};
  color: ${props => props.color};
  margin-right: 5px;
`
interface DeleteItem {
  active: boolean
}
const Delete = styled.div<DeleteItem>`
  width: 90px;
  height: 24px;
  font-size: 14px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #333;
  justify-content: center;
  background: #f2f3f5;
  transition: 0.3s;
  &:hover {
    background: #fff;
    .icon-delete {
      width: 12px;
      stroke: #f53f3f;
    }
  }
  .icon-delete {
    width: 12px;
    stroke: #f53f3f;
    fill: #f53f3f;
    transition: 0.3s;
    height: 12px;
    margin-right: 3px;
  }
`
